/**
 * Per-panel session storage.
 *
 * The admin console and the merchant portal keep entirely separate sessions.
 * They previously shared the `token` and `user` keys, so signing into one
 * silently overwrote the other — and on reload the surviving panel saw a token
 * belonging to the wrong role and signed itself out. Scoping the keys lets both
 * be signed in at once in the same browser.
 */

const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

const SCOPES = {
  admin: {
    token: 'pb.admin.token',
    user: 'pb.admin.user',
    activity: 'pb.admin.activity',
    extra: 'pb.admin.context',
    loginPath: '/admin/login'
  },
  merchant: {
    token: 'pb.merchant.token',
    user: 'pb.merchant.user',
    activity: 'pb.merchant.activity',
    extra: 'pb.merchant.context',
    loginPath: '/merchant/login'
  },
  courier: {
    token: 'pb.courier.token',
    user: 'pb.courier.user',
    activity: 'pb.courier.activity',
    extra: 'pb.courier.context',
    loginPath: '/courier/login'
  }
};

/** Keys from the single-session era; removed once so they cannot be picked up. */
const LEGACY_KEYS = ['token', 'user', 'lastActivity', 'merchant'];

const purgeLegacyKeys = () => {
  try {
    LEGACY_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage unavailable — nothing to purge */
  }
};
purgeLegacyKeys();

const keysFor = (scope) => SCOPES[scope] || SCOPES.admin;

export const loginPathFor = (scope) => keysFor(scope).loginPath;

export const getToken = (scope) => {
  try {
    return localStorage.getItem(keysFor(scope).token);
  } catch {
    return null;
  }
};

export const getUser = (scope) => {
  try {
    return JSON.parse(localStorage.getItem(keysFor(scope).user) || 'null');
  } catch {
    return null;
  }
};

export const getContext = (scope) => {
  try {
    return JSON.parse(localStorage.getItem(keysFor(scope).extra) || 'null');
  } catch {
    return null;
  }
};

export const startSession = (scope, { token, user, context } = {}) => {
  const keys = keysFor(scope);
  localStorage.setItem(keys.token, token);
  localStorage.setItem(keys.user, JSON.stringify(user || null));
  localStorage.setItem(keys.activity, Date.now().toString());
  if (context) localStorage.setItem(keys.extra, JSON.stringify(context));
};

export const clearSession = (scope) => {
  const keys = keysFor(scope);
  [keys.token, keys.user, keys.activity, keys.extra].forEach((key) =>
    localStorage.removeItem(key)
  );
};

export const updateActivity = (scope) => {
  localStorage.setItem(keysFor(scope).activity, Date.now().toString());
};

/** True while this panel holds a token that has not gone idle. */
export const checkSession = (scope) => {
  const keys = keysFor(scope);
  const token = localStorage.getItem(keys.token);
  if (!token) return false;

  const last = localStorage.getItem(keys.activity);
  if (!last) {
    localStorage.setItem(keys.activity, Date.now().toString());
    return true;
  }

  if (Date.now() - parseInt(last, 10) > SESSION_TIMEOUT) {
    clearSession(scope);
    return false;
  }

  localStorage.setItem(keys.activity, Date.now().toString());
  return true;
};

/** Which panel a browser path belongs to. The magic-link case page is public. */
export const scopeForPath = (pathname = '') => {
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/merchant/case/')) return null;
  if (pathname.startsWith('/merchant')) return 'merchant';
  // Only the signed-in portal, not the public /couriers marketing pages.
  if (pathname.startsWith('/courier/')) return 'courier';
  return null;
};

/**
 * Which token an outgoing request should carry.
 *
 * API prefixes decide it where they can (only an admin calls /api/admin, only a
 * merchant user calls /api/portal). Shared endpoints such as /api/auth/verify
 * are resolved by the panel currently on screen.
 */
export const scopeForRequest = (url = '', pathname = '') => {
  if (url.startsWith('/api/admin')) return 'admin';
  if (url.startsWith('/api/portal')) return 'merchant';
  if (url.startsWith('/api/courier-portal')) return 'courier';

  const byPath = scopeForPath(pathname);
  if (byPath) return byPath;

  if (getToken('admin')) return 'admin';
  if (getToken('merchant')) return 'merchant';
  if (getToken('courier')) return 'courier';
  return null;
};
